import Linkify from 'linkify-react';
import React from 'react';

import getConfig from 'getConfig';
import BenchLink from 'link-preview/BenchLink';
import PlaceholderText from 'ui-library/PlaceholderText';

/**
 * The max length of the text value that we should allow for checking the text for
 * a URL. This was added as we saw a potential slowdown of the UI when rendering
 * text fields with large character counts.
 */
export const MAX_LENGTH_FOR_URL_CHECKING = 10000;

interface UrlAwareTextProps {
  textValue?: string | null;
  className?: string;
  placeholder?: string | null;
  breakNewLines?: boolean;
}

// Allowed-list of most popular top-level domains
// Source: https://www.icdsoft.com/blog/what-are-the-most-popular-tlds-domain-extensions/
export const ALLOWED_LISTED_TOP_LEVEL_DOMAINS = [
  'com',
  'net',
  'org',
  'edu',
  'gov',
  'de',
  'icu',
  'uk',
  'ru',
  'info',
  'top',
  'xyz',
  'tk',
  'cn',
  'ga',
  'cf',
  'nl',
];

const regexExpression = `[\\w.]*[.](${ALLOWED_LISTED_TOP_LEVEL_DOMAINS.join('|')})\\b`;
const hasAllowedListedTLDRegex = new RegExp(regexExpression);

// If the url does not have an allowed-listed top-level domain, linkify it only if it has a valid prefix.
// Linkify always passes valid urls to this function, so this function just filters urls to make linkification
// less permissive
function isURLAcceptable(value: string, type: string): boolean {
  if (type !== 'url') {
    return true;
  }
  const hasValidPrefix = /^((http|ftp)s?:\/\/|www)/.test(value.toLowerCase());
  const hasAllowedListedTLD = hasAllowedListedTLDRegex.test(value.toLowerCase());
  return hasValidPrefix || hasAllowedListedTLD;
}

// TODO BNCH-58980: Refactor LinkifyWithCustomURLProtocolSupport to use Linkify's built in
// custom protocols to recognize custom links rather than using <a> tags
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderAsBenchLink(payload: any): JSX.Element {
  return <BenchLink href={payload.attributes.href}>{payload.content}</BenchLink>;
}

/**
 * Splits a given string into substrings that represent URLs with supported custom protocols (e.g.,
 * com.adobe.adobe-reader, registered by Adobe Reader), and those that do not. Linkify does not support
 * custom protocols, so we need to convert those URLs into <a> tags directly.
 *
 * E.g., if getConfig('CUSTOM_URL_PROTOCOL_ALLOW_LIST') === ['com.adobe.adobe-reader']:
 *
 * <LinkifyWithCustomURLProtocolSupport textValue='Click on me: com.adobe.adobe-reader://launch'/> ===
 *  <>
 *    <Linkify>Click on me: </Linkify>
 *    <a href='com.adobe.adobe-reader://launch'>com.adobe.adobe-reader://launch</a>
 *  </>
 */
function LinkifyWithCustomURLProtocolSupport(props: {
  textValue: string;
  breakNewLines?: boolean;
}): JSX.Element {
  const {textValue} = props;
  const customUrlProtocols = getConfig('CUSTOM_URL_PROTOCOL_ALLOW_LIST');
  const linkifyOptions = {
    defaultProtocol: 'https',
    validate: isURLAcceptable,
    nl2br: props.breakNewLines,
    render: renderAsBenchLink,
  };
  if (!customUrlProtocols.length) {
    return <Linkify options={linkifyOptions}>{textValue}</Linkify>;
  }
  const regex = new RegExp(`((?:${customUrlProtocols.join('|')})://\\S+)`);
  const textParts = textValue.split(regex).filter((str) => Boolean(str));
  return (
    <>
      {textParts.map((part, i) => {
        if (regex.test(part)) {
          return (
            <a key={i} href={part}>
              {part}
            </a>
          );
        }
        return (
          <Linkify key={i} options={linkifyOptions}>
            {part}
          </Linkify>
        );
      })}
    </>
  );
}

/**
 * Component for rendering URL-aware text
 */
export default function UrlAwareText(props: UrlAwareTextProps): JSX.Element {
  if (props.textValue && props.textValue.length > MAX_LENGTH_FOR_URL_CHECKING) {
    return <span className={props.className}>{props.textValue}</span>;
  } else if (props.textValue) {
    return (
      <span className={props.className}>
        <LinkifyWithCustomURLProtocolSupport
          textValue={props.textValue}
          breakNewLines={props.breakNewLines}
        />
      </span>
    );
  } else if (props.placeholder) {
    return <PlaceholderText value={props.placeholder} />;
  } else {
    return <span className={props.className} />;
  }
}
